import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Styles from "../lets_talk/Letstalk.module.css";
import SearchInput from "../../components/searchinput/SearchInput";
import DataTable from "../../components/datatable/DataTable";
import Pagination from "../../components/pagination/Pagination";
import moment from "moment";
import dayjs from "dayjs";
import isSameOrOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AxiosInstance } from "../../axios";
import Buttons from "../../components/buttons/Buttons";
import { plus } from "../../controller/Images";

// Extend dayjs with necessary plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrOrBefore);
dayjs.extend(customParseFormat);

// Department data
const departmentData = [
  { id: 1, name: "Software Development" },
  { id: 2, name: "Quality Assurance" },
  { id: 3, name: "Cloud Engineering" },
  { id: 4, name: "IT Project Management" },
  { id: 5, name: "Business Administration" },
  { id: 6, name: "Accounts" },
  { id: 7, name: "Human Resource Development" },
  { id: 8, name: "Product Design" },
];

// Initial filter template
const INITIAL_FILTERS = {
  search: "",
  createdFromDate: "",
  createdToDate: "",
  postedFromDate: "",
  postedToDate: "",
  createdBy: "",
  jobStatus: "",
  approvalStatus: "",
  department: "",
};

const Admin_Join_Our_Team = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // =====================================================================
  // FILTER STATE MANAGEMENT & INITIALIZATION
  // =====================================================================

  // Helper to initialize filters from URL params OR location.state
  const initializeFilters = useCallback(() => {
    // Check if returning from another page with saved filters
    if (location.state?.jobFilters) {
      return location.state.jobFilters;
    }

    // Otherwise, initialize from URL search params
    return {
      search: searchParams.get("search") || "",
      createdFromDate: searchParams.get("createdFromDate") || "",
      createdToDate: searchParams.get("createdToDate") || "",
      postedFromDate: searchParams.get("postedFromDate") || "",
      postedToDate: searchParams.get("postedToDate") || "",
      createdBy: searchParams.get("createdBy") || "",
      jobStatus: searchParams.get("jobStatus") || "",
      approvalStatus: searchParams.get("approvalStatus") || "",
      department: searchParams.get("department") || "",
    };
  }, [location.state, searchParams]);

  // Main filter state - initialized from URL params or location.state
  const [filters, setFilters] = useState(() => initializeFilters());

  // Temporary states for filter popups
  const [tempFilters, setTempFilters] = useState({
    createdFromDate: "",
    createdToDate: "",
    postedFromDate: "",
    postedToDate: "",
    dateLabel: "",
    createdBy: "",
    jobStatus: "",
    approvalStatus: "",
    department: "",
  });

  const [chipData, setChipData] = useState([]);
  const [isOpen, setIsOpen] = useState({ status: false, column: "" });
  const filterRef = useRef(null);

  // Pagination state
  const initialCurrentPage = Number(searchParams.get("page")) || 1;
  const initialRowsPerPage = Number(searchParams.get("limit")) || 10;
  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const rowsPerPageOptions = [10, 15, 20, 50];

  // Data state
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const columns = useMemo(
    () => [
      { header: "S.No", accessor: "sno" },
      { header: "Job ID", accessor: "job_id", sortable: true },
      { header: "Role", accessor: "role", sortable: true },
      {
        header: "Department",
        accessor: "department",
        sortable: true,
        filterable: true,
      },
      {
        header: "Candidate Applied",
        accessor: "candidate_applied",
        sortable: false,
      },
      {
        header: "Created Date",
        accessor: "created_date",
        sortable: true,
        filterable: true,
        isDateColumn: true,
      },
      {
        header: "Posted Date",
        accessor: "posted_date",
        sortable: true,
        filterable: true,
        isDateColumn: true,
      },
      {
        header: "Created By",
        accessor: "created_by_name",
        sortable: true,
        filterable: true,
      },
      {
        header: "Job Status",
        accessor: "job_status",
        sortable: true,
        filterable: true,
      },
      {
        header: "Approval Status",
        accessor: "approval_status",
        sortable: true,
        filterable: true,
      },
    ],
    [],
  );

  const filterOptions = useMemo(
    () => ({
      created_by_name: [
        { label: "Super Admin", value: "Super Admin" },
        { label: "Admin", value: "Admin" },
      ],
      job_status: [
        { label: "Waiting for Approval", value: "Waiting for Approval" },
        { label: "Inactive", value: "Inactive" },
        { label: "Active  ", value: "Active " },
      ],
      approval_status: [
        { label: "Waiting for Approval", value: "Waiting for Approval" },
        { label: "On Hold", value: "On Hold" },
        { label: "Approved", value: "Approved" },
        { label: "Reject", value: "Reject" },
      ],
      department: departmentData.map((dept) => ({
        label: dept.name,
        value: dept.name,
      })),
    }),
    [],
  );

  // =====================================================================
  // EFFECT: Restore filters from location.state when returning from Job View
  // =====================================================================
  useEffect(() => {
    if (location.state?.from === "jobpage" && location.state?.jobFilters) {
      setFilters((prev) => ({
        ...prev,
        ...location.state.jobFilters,
      }));
      setCurrentPage(1);
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.from, location.state?.jobFilters]);

  // =====================================================================
  // SINGLE API CALL EFFECT - Only when filters change
  // =====================================================================
  useEffect(() => {
    const abortController = new AbortController();

    const executeApiCall = async () => {
      setLoading(true);
      try {
        // Helper function to map categorical filter strings to numbers for API payload
        const mapCategoricalValueToNumber = (column, value) => {
          if (!value) return "";
          const options = filterOptions[column];
          if (options) {
            const option = options.find(
              (opt) => opt.label === value || opt.value === value,
            );
            if (option) {
              if (column === "created_by_name") {
                return option.value === "Super Admin"
                  ? "Super Admin"
                  : option.value === "Admin"
                    ? "Admin"
                    : "";
              } else if (column === "job_status") {
                switch (option.value) {
                  case "Waiting for Approval":
                    return 0;
                  case "Inactive":
                    return 1;
                  case "Active  ":
                    return 2;
                  default:
                    return "";
                }
              } else if (column === "approval_status") {
                switch (option.value) {
                  case "Waiting for Approval":
                    return 0;
                  case "On Hold":
                    return 1;
                  case "Approved":
                    return 2;
                  case "Reject":
                    return 3;
                  default:
                    return "";
                }
              } else if (column === "department") {
                const dept = departmentData.find((d) => d.name === value);
                return dept ? dept.id : "";
              }
            }
          }
          return "";
        };

        const payload = {
          search: filters.search,
          from_created_date: filters.createdFromDate
            ? moment(filters.createdFromDate, "DD/MM/YYYY").format(
                "YYYY-MM-DD HH:mm:ss",
              )
            : "",
          to_created_date: filters.createdToDate
            ? moment(filters.createdToDate, "DD/MM/YYYY").format(
                "YYYY-MM-DD HH:mm:ss",
              )
            : "",
          post_startdate: filters.postedFromDate
            ? moment(filters.postedFromDate, "DD/MM/YYYY").format(
                "YYYY-MM-DD HH:mm:ss",
              )
            : "",
          post_enddate: filters.postedToDate
            ? moment(filters.postedToDate, "DD/MM/YYYY").format(
                "YYYY-MM-DD HH:mm:ss",
              )
            : "",
          created_by_name: mapCategoricalValueToNumber(
            "created_by_name",
            filters.createdBy,
          ),
          job_status: mapCategoricalValueToNumber(
            "job_status",
            filters.jobStatus,
          ),
          approval_status: mapCategoricalValueToNumber(
            "approval_status",
            filters.approvalStatus,
          ),
          department_id: mapCategoricalValueToNumber(
            "department",
            filters.department,
          ),
        };

        const res = await AxiosInstance.post(
          "/api/joinourteam/job/list",
          payload,
          {
            signal: abortController.signal,
          },
        );

        // Only process if request wasn't aborted
        if (!abortController.signal.aborted) {
          let jobList = Array.isArray(res?.data.data) ? res?.data.data : [];

          // Normalize API response data for client-side filtering
          jobList = jobList.map((job) => {
            const newJob = { ...job };

            // Normalize created_by_name
            if (typeof newJob.created_by_name === "number") {
              switch (newJob.created_by_name) {
                case 1:
                  newJob.created_by_name = "Super Admin";
                  break;
                case 2:
                  newJob.created_by_name = "Admin";
                  break;
                default:
                  break;
              }
            }

            // Normalize job_status
            if (typeof newJob.job_status === "number") {
              switch (newJob.job_status) {
                case 0:
                  newJob.job_status = "Waiting for Approval";
                  break;
                case 1:
                  newJob.job_status = "Inactive";
                  break;
                case 2:
                  newJob.job_status = "Active ";
                  break;
                default:
                  break;
              }
            }

            // Normalize approval_status
            if (typeof newJob.approval_status === "number") {
              switch (newJob.approval_status) {
                case 0:
                  newJob.approval_status = "Waiting for Approval";
                  break;
                case 1:
                  newJob.approval_status = "On Hold";
                  break;
                case 2:
                  newJob.approval_status = "Approved";
                  break;
                case 3:
                  newJob.approval_status = "Reject";
                  break;
                default:
                  break;
              }
            }

            // Normalize 'department' property
            if (typeof newJob.department_id === "number") {
              const dept = departmentData.find(
                (d) => d.id === newJob.department_id,
              );
              newJob.department = dept ? dept.name : "Unknown Department";
            } else if (newJob.department_name) {
              newJob.department = newJob.department_name;
            } else {
              newJob.department = "-";
            }

            delete newJob.department_id;
            delete newJob.department_name;

            return newJob;
          });

          setAllJobs(jobList);
        }
      } catch (err) {
        if (!abortController.signal.aborted) {
          console.error("Error fetching jobs:", err);
          setAllJobs([]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    executeApiCall();

    // Cleanup function - abort the request if component unmounts or effect re-runs
    return () => {
      abortController.abort();
    };
  }, [filters, filterOptions]);

  // =====================================================================
  // EFFECT: Update URL params whenever filters, currentPage, or rowsPerPage change
  // =====================================================================
  useEffect(() => {
    const params = new URLSearchParams();

    // Add filter params
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });

    // Add pagination params
    if (currentPage !== 1) params.set("page", currentPage.toString());
    if (rowsPerPage !== 10) params.set("limit", rowsPerPage.toString());

    setSearchParams(params);
  }, [filters, currentPage, rowsPerPage, setSearchParams]);

  // =====================================================================
  // EFFECT: Update chip data when filters change
  // =====================================================================
  useEffect(() => {
    const chips = [];

    if (filters.search) {
      chips.push({
        type: "search",
        value: filters.search,
        label: `Search: "${filters.search}"`,
      });
    }

    if (filters.createdFromDate || filters.createdToDate) {
      let label =
        filters.createdFromDate && filters.createdToDate
          ? `Created Date: ${filters.createdFromDate} - ${filters.createdToDate}`
          : filters.createdFromDate
            ? `Created From: ${filters.createdFromDate}`
            : `Created To: ${filters.createdToDate}`;
      chips.push({
        label,
        type: "created_date",
        value: `${filters.createdFromDate}-${filters.createdToDate}`,
      });
    }

    if (filters.postedFromDate || filters.postedToDate) {
      let label =
        filters.postedFromDate && filters.postedToDate
          ? `Posted Date: ${filters.postedFromDate} - ${filters.postedToDate}`
          : filters.postedFromDate
            ? `Posted From: ${filters.postedFromDate}`
            : `Posted To: ${filters.postedToDate}`;
      chips.push({
        label,
        type: "posted_date",
        value: `${filters.postedFromDate}-${filters.postedToDate}`,
      });
    }

    if (filters.createdBy) {
      chips.push({
        label: `Created By: ${filters.createdBy}`,
        type: "created_by_name",
        value: filters.createdBy,
      });
    }

    if (filters.jobStatus) {
      chips.push({
        label: `Job Status: ${filters.jobStatus}`,
        type: "job_status",
        value: filters.jobStatus,
      });
    }

    if (filters.approvalStatus) {
      chips.push({
        label: `Approval Status: ${filters.approvalStatus}`,
        type: "approval_status",
        value: filters.approvalStatus,
      });
    }

    if (filters.department) {
      chips.push({
        label: `Department: ${filters.department}`,
        type: "department",
        value: filters.department,
      });
    }

    setChipData(chips);
  }, [filters]);

  // =====================================================================
  // EFFECT: Handle click outside filter popup
  // =====================================================================
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(e.target) &&
        !e.target.closest('img[alt="filter"]')
      ) {
        setIsOpen((prev) => {
          if (prev.status) {
            setTempFilters({
              createdFromDate: filters.createdFromDate,
              createdToDate: filters.createdToDate,
              postedFromDate: filters.postedFromDate,
              postedToDate: filters.postedToDate,
              dateLabel: "",
              createdBy: filters.createdBy,
              jobStatus: filters.jobStatus,
              approvalStatus: filters.approvalStatus,
              department: filters.department,
            });
          }
          return { ...prev, status: false };
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [filters]);

  // =====================================================================
  // FILTER & CHIP HANDLERS
  // =====================================================================

  const handleCreateJobClick = () => {
    navigate("/admin/job-create");
  };

  const handleSearch = useCallback((value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setCurrentPage(1);
  }, []);

  const handleDeleteChip = useCallback((chip) => {
    setFilters((prev) => {
      const updated = { ...prev };
      switch (chip.type) {
        case "search":
          updated.search = "";
          break;
        case "created_date":
          updated.createdFromDate = "";
          updated.createdToDate = "";
          break;
        case "posted_date":
          updated.postedFromDate = "";
          updated.postedToDate = "";
          break;
        case "created_by_name":
          updated.createdBy = "";
          break;
        case "job_status":
          updated.jobStatus = "";
          break;
        case "approval_status":
          updated.approvalStatus = "";
          break;
        case "department":
          updated.department = "";
          break;
        default:
          break;
      }
      return updated;
    });
    setCurrentPage(1);
  }, []);

  const handleAllClear = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setTempFilters({
      createdFromDate: "",
      createdToDate: "",
      postedFromDate: "",
      postedToDate: "",
      dateLabel: "",
      createdBy: "",
      jobStatus: "",
      approvalStatus: "",
      department: "",
    });
    setCurrentPage(1);
  }, []);

  const handleFilterClick = useCallback(
    (col) => {
      setIsOpen({ status: true, column: col });

      if (col === "created_date") {
        setTempFilters((prev) => ({
          ...prev,
          createdFromDate: filters.createdFromDate,
          createdToDate: filters.createdToDate,
        }));
        const chip = chipData.find((c) => c.type === "created_date");
        let currentLabel = "";
        if (chip) {
          currentLabel = chip.label.replace(/^Created Date:\s*|-$/, "").trim();
        } else if (filters.createdFromDate && filters.createdToDate) {
          currentLabel = `${filters.createdFromDate} - ${filters.createdToDate}`;
        } else if (filters.createdFromDate) {
          currentLabel = `From ${filters.createdFromDate}`;
        } else if (filters.createdToDate) {
          currentLabel = `To ${filters.createdToDate}`;
        }
        setTempFilters((prev) => ({ ...prev, dateLabel: currentLabel }));
      } else if (col === "posted_date") {
        setTempFilters((prev) => ({
          ...prev,
          postedFromDate: filters.postedFromDate,
          postedToDate: filters.postedToDate,
        }));
        const chip = chipData.find((c) => c.type === "posted_date");
        let currentLabel = "";
        if (chip) {
          currentLabel = chip.label.replace(/^Posted Date:\s*|-$/, "").trim();
        } else if (filters.postedFromDate && filters.postedToDate) {
          currentLabel = `${filters.postedFromDate} - ${filters.postedToDate}`;
        } else if (filters.postedFromDate) {
          currentLabel = `From ${filters.postedFromDate}`;
        } else if (filters.postedToDate) {
          currentLabel = `To ${filters.postedToDate}`;
        }
        setTempFilters((prev) => ({ ...prev, dateLabel: currentLabel }));
      } else if (
        col === "created_by_name" ||
        col === "job_status" ||
        col === "approval_status" ||
        col === "department"
      ) {
        const filterMap = {
          created_by_name: "createdBy",
          job_status: "jobStatus",
          approval_status: "approvalStatus",
          department: "department",
        };
        const filterKey = filterMap[col];
        setTempFilters((prev) => ({
          ...prev,
          [filterKey]: filters[filterKey],
        }));
      } else {
        setTempFilters(INITIAL_FILTERS);
      }
    },
    [filters, chipData],
  );

  const handleApplyFilter = useCallback(() => {
    setFilters((prev) => {
      const updated = { ...prev };
      switch (isOpen.column) {
        case "created_date":
          updated.createdFromDate = tempFilters.createdFromDate;
          updated.createdToDate = tempFilters.createdToDate;
          break;
        case "posted_date":
          updated.postedFromDate = tempFilters.postedFromDate;
          updated.postedToDate = tempFilters.postedToDate;
          break;
        case "created_by_name":
          updated.createdBy = tempFilters.createdBy;
          break;
        case "job_status":
          updated.jobStatus = tempFilters.jobStatus;
          break;
        case "approval_status":
          updated.approvalStatus = tempFilters.approvalStatus;
          break;
        case "department":
          updated.department = tempFilters.department;
          break;
        default:
          break;
      }
      return updated;
    });
    setIsOpen({ status: false, column: "" });
    setCurrentPage(1);
  }, [isOpen.column, tempFilters]);

  const onDateRangeApply = useCallback(
    ({ fromDate: from, toDate: to, label }) => {
      setFilters((prev) => {
        const updated = { ...prev };
        if (isOpen.column === "created_date") {
          updated.createdFromDate = from;
          updated.createdToDate = to;
        } else if (isOpen.column === "posted_date") {
          updated.postedFromDate = from;
          updated.postedToDate = to;
        }
        return updated;
      });

      setTempFilters((prev) => ({
        ...prev,
        ...(isOpen.column === "created_date"
          ? { createdFromDate: from, createdToDate: to }
          : { postedFromDate: from, postedToDate: to }),
        dateLabel: label,
      }));

      setIsOpen({ status: false, column: "" });
      setCurrentPage(1);
    },
    [isOpen.column],
  );

  const handleCategoricalFilterChange = useCallback((column, value) => {
    const filterMap = {
      created_by_name: "createdBy",
      job_status: "jobStatus",
      approval_status: "approvalStatus",
      department: "department",
    };
    const filterKey = filterMap[column];
    setTempFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
  }, []);

  const onClearSpecificFilter = useCallback((col) => {
    setFilters((prev) => {
      const updated = { ...prev };
      switch (col) {
        case "created_date":
          updated.createdFromDate = "";
          updated.createdToDate = "";
          break;
        case "posted_date":
          updated.postedFromDate = "";
          updated.postedToDate = "";
          break;
        case "created_by_name":
          updated.createdBy = "";
          break;
        case "job_status":
          updated.jobStatus = "";
          break;
        case "approval_status":
          updated.approvalStatus = "";
          break;
        case "department":
          updated.department = "";
          break;
        default:
          break;
      }
      return updated;
    });

    setTempFilters(INITIAL_FILTERS);
    setIsOpen({ status: false, column: "" });
    setCurrentPage(1);
  }, []);

  // =====================================================================
  // NAVIGATION WITH FILTERS
  // =====================================================================
  const handleViewClick = useCallback(
    (job) => {
      navigate("/admin/job-view", {
        state: {
          jobId: job.job_id,
          jobFilters: filters,
          from: "jobpage",
        },
      });
    },
    [navigate, filters],
  );

  // =====================================================================
  // DATA PROCESSING & FILTERING
  // =====================================================================

  const filteredJobs = useMemo(() => {
    let jobs = allJobs;

    // Search filter
    if (filters.search) {
      jobs = jobs.filter(
        (job) =>
          job.job_id?.toLowerCase().includes(filters.search.toLowerCase()) ||
          job.role?.toLowerCase().includes(filters.search.toLowerCase()) ||
          job.department
            ?.toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          job.created_by_name
            ?.toLowerCase()
            .includes(filters.search.toLowerCase()),
      );
    }

    // Created Date filter
    if (filters.createdFromDate && filters.createdToDate) {
      const start = dayjs(filters.createdFromDate, "DD/MM/YYYY").startOf("day");
      const end = dayjs(filters.createdToDate, "DD/MM/YYYY").endOf("day");
      jobs = jobs.filter(
        (job) =>
          job.created_date &&
          dayjs(job.created_date).isSameOrAfter(start) &&
          dayjs(job.created_date).isSameOrBefore(end),
      );
    } else if (filters.createdFromDate) {
      const start = dayjs(filters.createdFromDate, "DD/MM/YYYY").startOf("day");
      jobs = jobs.filter(
        (job) =>
          job.created_date && dayjs(job.created_date).isSameOrAfter(start),
      );
    } else if (filters.createdToDate) {
      const end = dayjs(filters.createdToDate, "DD/MM/YYYY").endOf("day");
      jobs = jobs.filter(
        (job) =>
          job.created_date && dayjs(job.created_date).isSameOrBefore(end),
      );
    }

    // Posted Date filter
    if (filters.postedFromDate && filters.postedToDate) {
      const start = dayjs(filters.postedFromDate, "DD/MM/YYYY").startOf("day");
      const end = dayjs(filters.postedToDate, "DD/MM/YYYY").endOf("day");
      jobs = jobs.filter(
        (job) =>
          job.posted_date &&
          dayjs(job.posted_date).isSameOrAfter(start) &&
          dayjs(job.posted_date).isSameOrBefore(end),
      );
    } else if (filters.postedFromDate) {
      const start = dayjs(filters.postedFromDate, "DD/MM/YYYY").startOf("day");
      jobs = jobs.filter(
        (job) => job.posted_date && dayjs(job.posted_date).isSameOrAfter(start),
      );
    } else if (filters.postedToDate) {
      const end = dayjs(filters.postedToDate, "DD/MM/YYYY").endOf("day");
      jobs = jobs.filter(
        (job) => job.posted_date && dayjs(job.posted_date).isSameOrBefore(end),
      );
    }

    // Categorical filters
    if (filters.createdBy) {
      jobs = jobs.filter((job) => job.created_by_name === filters.createdBy);
    }
    if (filters.jobStatus) {
      jobs = jobs.filter((job) => job.job_status === filters.jobStatus);
    }
    if (filters.approvalStatus) {
      jobs = jobs.filter(
        (job) => job.approval_status === filters.approvalStatus,
      );
    }
    if (filters.department) {
      jobs = jobs.filter((job) => job.department === filters.department);
    }

    return jobs;
  }, [allJobs, filters]);

  const totalFilteredItems = filteredJobs.length;

  const paginatedJobs = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredJobs.slice(start, end);
  }, [filteredJobs, currentPage, rowsPerPage]);

  const actionIconProps = useMemo(
    () => ({
      defaultIcon: "",
      hoverIcon: "",
      onClick: handleViewClick,
    }),
    [handleViewClick],
  );

  const processedJobs = useMemo(
    () =>
      paginatedJobs.map((job, idx) => ({
        sno: (currentPage - 1) * rowsPerPage + idx + 1,
        job_id: job.job_id || "-",
        role: job.role || "-",
        department: job.department || "-",
        candidate_applied:
          job.candidate_count !== undefined ? job.candidate_count : "-",
        created_date: job.created_date
          ? moment(job.created_date).format("DD/MM/YYYY")
          : "-",
        posted_date: job.posted_date
          ? moment(job.posted_date).format("DD/MM/YYYY")
          : job.created_date
            ? moment(job.created_date).format("DD/MM/YYYY")
            : "-",
        created_by_name: job.created_by_name || "-",
        job_status: job.job_status !== undefined ? job.job_status : "-",
        approval_status:
          job.approval_status !== undefined ? job.approval_status : "-",
        originalData: job,
      })),
    [paginatedJobs, currentPage, rowsPerPage],
  );

  // =====================================================================
  // RENDER
  // =====================================================================

  return (
    <div className={`container-fluid px-3 ${Styles.main_Container}`}>
      <div className="row mb-3">
        <div className="col-12 col-md-6 d-flex align-items-center">
          <SearchInput
            handleKeyDown={handleSearch}
            placeholder="Search with Job ID, Role, Department, Created By..."
            value={filters.search}
          />
        </div>
        <div className="col-12 col-md-6 d-flex justify-content-end align-items-center">
          <Buttons
            text="Create New Job"
            type="button"
            variant="secondary"
            icon={
              <img
                src={plus}
                alt="plus"
                style={{ width: "25px", height: "25px" }}
              />
            }
            onClick={handleCreateJobClick}
          />
        </div>
      </div>

      <div className="mb-3">
        {chipData.map((chip, index) => (
          <span
            key={index}
            className="custom-badge  me-2 rounded-md flex items-center"
          >
            {chip.label}
            <button
              onClick={() => handleDeleteChip(chip)}
              className="custom-close-btn "
              aria-label="Remove filter"
            ></button>
          </span>
        ))}
        {chipData.length > 0 && (
          <button className="Clearbtn" onClick={handleAllClear}>
            Clear All Filters
          </button>
        )}
      </div>

      <DataTable
        loading={loading}
        columns={columns}
        data={processedJobs}
        filterOptions={filterOptions}
        filters={{
          created_date_from: filters.createdFromDate,
          created_date_to: filters.createdToDate,
          posted_date_from: filters.postedFromDate,
          posted_date_to: filters.postedToDate,
          created_by_name: filters.createdBy,
          job_status: filters.jobStatus,
          approval_status: filters.approvalStatus,
          department: filters.department,
        }}
        actionIcon={actionIconProps}
        handleFilter={handleFilterClick}
        isOpens={isOpen}
        filterPopupRef={filterRef}
        currentDateRange={
          isOpen.status &&
          (isOpen.column === "created_date" || isOpen.column === "posted_date")
            ? {
                fromDate:
                  isOpen.column === "created_date"
                    ? tempFilters.createdFromDate
                    : tempFilters.postedFromDate,
                toDate:
                  isOpen.column === "created_date"
                    ? tempFilters.createdToDate
                    : tempFilters.postedToDate,
                label: tempFilters.dateLabel,
              }
            : null
        }
        onDateRangeApply={onDateRangeApply}
        tempCategoricalFilters={{
          created_by_name: tempFilters.createdBy,
          job_status: tempFilters.jobStatus,
          approval_status: tempFilters.approvalStatus,
          department: tempFilters.department,
        }}
        onCategoricalFilterChange={handleCategoricalFilterChange}
        handleApplyFilter={handleApplyFilter}
        onClearSpecificFilter={onClearSpecificFilter}
        ellipsisIndex={[2, 4, 3]}
      />

      <div
        className="mb-3"
        style={{ display: "flex", justifyContent: "flex-end" }}
      >
        <Pagination
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          totalItems={totalFilteredItems}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      </div>
    </div>
  );
};

export default Admin_Join_Our_Team;